import { useState } from 'react';
import { Button } from '../components/ui';
import { supportAPI } from '../services/api';

type TabType = 'faq' | 'contact' | 'bug';

interface FAQItem {
  question: string;
  answer: string;
  category: 'getting-started' | 'contacts' | 'communication' | 'account';
}

const faqs: FAQItem[] = [
  {
    category: 'getting-started',
    question: 'What is OnDeck?',
    answer: 'OnDeck is a relationship CRM designed for real estate agents. It helps you stay on top of your contacts by generating a daily "lineup" of people you should reach out to, using baseball-themed stages to track where each contact is in your relationship pipeline.',
  },
  {
    category: 'getting-started',
    question: 'How does the daily lineup work?',
    answer: 'Each day, OnDeck analyzes your contacts based on factors like how long since you last contacted them, upcoming important dates, and their stage in your pipeline. It generates a prioritized list of who to contact that day, helping you stay proactive with your relationships.',
  },
  {
    category: 'getting-started',
    question: 'What do the baseball stages mean?',
    answer: 'On Deck = New leads warming up. At Bat = Actively showing properties. On Base = In active conversations. Scoring Position = Under contract. Hall of Fame = Closed deals (past clients). Bench = Cold or inactive leads.',
  },
  {
    category: 'contacts',
    question: 'How do I add contacts?',
    answer: 'You can add contacts individually by clicking "+ Add Contact" on the Contacts page, or import multiple contacts at once using the "Import CSV" feature.',
  },
  {
    category: 'contacts',
    question: 'What are tags and how do I use them?',
    answer: 'Tags help you organize and categorize your contacts. You can create custom tags with colors (like "First-time buyer" or "Investor") and apply multiple tags to each contact. Tags with AI context can provide automated insights about contacts with that tag.',
  },
  {
    category: 'contacts',
    question: 'How do I move a contact to a different stage?',
    answer: 'Open the contact detail page and click "Edit" to change their stage. You can also drag contacts between stages in certain views.',
  },
  {
    category: 'communication',
    question: 'How do I log a call or interaction?',
    answer: 'On any contact detail page, click the "Log" button to record a call, meeting, or other interaction. This helps OnDeck understand your relationship history and prioritize future outreach.',
  },
  {
    category: 'communication',
    question: 'How does messaging work?',
    answer: 'OnDeck integrates with your native messaging apps. Click the message icon to open a text message in your default SMS app. You can also draft messages within OnDeck and copy them to send.',
  },
  {
    category: 'account',
    question: 'How do I change my work hours?',
    answer: 'Go to Settings and scroll to the "Work Hours" section. Here you can set your daily schedule and which days you work. OnDeck uses this to show motivational messages and countdown timers during your work hours.',
  },
  {
    category: 'account',
    question: 'How do I switch between light and dark mode?',
    answer: 'Click the theme icon in the top navigation bar to cycle through Light, Dark, and System (which follows your device settings).',
  },
];

const categories = [
  { id: 'all', label: 'All Topics' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'communication', label: 'Communication' },
  { id: 'account', label: 'Account & Settings' },
];

export default function Help() {
  const [activeTab, setActiveTab] = useState<TabType>('faq');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [contactForm, setContactForm] = useState({ subject: '', message: '' });
  const [bugForm, setBugForm] = useState({
    title: '',
    severity: '',
    page: '',
    steps: '',
    expected: '',
    actual: '',
    browser: typeof navigator !== 'undefined' ? navigator.userAgent.split(' ').slice(-1)[0] : '',
  });
  const [submitted, setSubmitted] = useState<'contact' | 'bug' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const filteredFaqs = activeCategory === 'all'
    ? faqs
    : faqs.filter(faq => faq.category === activeCategory);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await supportAPI.submitRequest({
        subject: contactForm.subject as 'feature' | 'account' | 'billing' | 'other',
        message: contactForm.message,
      });

      if (result.success) {
        setSubmitted('contact');
        setContactForm({ subject: '', message: '' });
      } else {
        setSubmitError(result.error?.message || 'Failed to submit request');
      }
    } catch (error) {
      setSubmitError('Network error. Please try again.');
      console.error('Support request error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBugSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await supportAPI.submitBugReport({
        title: bugForm.title,
        severity: bugForm.severity as 'critical' | 'high' | 'medium' | 'low',
        page: bugForm.page,
        steps: bugForm.steps,
        expected: bugForm.expected || undefined,
        actual: bugForm.actual || undefined,
        browser: bugForm.browser || undefined,
      });

      if (result.success) {
        setSubmitted('bug');
        setBugForm({
          title: '',
          severity: '',
          page: '',
          steps: '',
          expected: '',
          actual: '',
          browser: typeof navigator !== 'undefined' ? navigator.userAgent.split(' ').slice(-1)[0] : '',
        });
      } else {
        setSubmitError(result.error?.message || 'Failed to submit bug report');
      }
    } catch (error) {
      setSubmitError('Network error. Please try again.');
      console.error('Bug report error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Help & Support</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Find answers to common questions or get in touch with our team
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-8 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => { setActiveTab('faq'); setSubmitted(null); setSubmitError(null); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'faq'
              ? 'border-ondeck-500 text-ondeck-600 dark:text-ondeck-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <span className="mr-2">📖</span>
          FAQ
        </button>
        <button
          onClick={() => { setActiveTab('bug'); setSubmitted(null); setSubmitError(null); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'bug'
              ? 'border-ondeck-500 text-ondeck-600 dark:text-ondeck-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <span className="mr-2">🐛</span>
          Report a Bug
        </button>
        <button
          onClick={() => { setActiveTab('contact'); setSubmitted(null); setSubmitError(null); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'contact'
              ? 'border-ondeck-500 text-ondeck-600 dark:text-ondeck-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <span className="mr-2">💬</span>
          Contact Support
        </button>
      </div>

      {/* FAQ Section */}
      {activeTab === 'faq' && (
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Frequently Asked Questions
          </h2>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="space-y-3">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <button
                  onClick={() => setExpandedId(expandedId === index ? null : index)}
                  className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <span className="font-medium text-gray-900 dark:text-white pr-4">
                    {faq.question}
                  </span>
                  <span className={`text-gray-400 transition-transform ${expandedId === index ? 'rotate-180' : ''}`}>
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>
                </button>
                {expandedId === index && (
                  <div className="px-4 pb-4 text-gray-600 dark:text-gray-400">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Bug Report Section */}
      {activeTab === 'bug' && (
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">🐛</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Report a Bug</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Found something that isn't working correctly? Help us fix it by providing details below.
          </p>

          {submitted === 'bug' ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🎉</div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Bug Report Submitted!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Thank you for helping us improve OnDeck. We'll investigate this issue.
              </p>
              <Button onClick={() => setSubmitted(null)}>Report Another Bug</Button>
            </div>
          ) : (
            <form onSubmit={handleBugSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bug Title *
                  </label>
                  <input
                    type="text"
                    value={bugForm.title}
                    onChange={(e) => setBugForm({ ...bugForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500"
                    placeholder="Brief description of the issue"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Severity *
                  </label>
                  <select
                    value={bugForm.severity}
                    onChange={(e) => setBugForm({ ...bugForm, severity: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500"
                    required
                  >
                    <option value="">Select severity...</option>
                    <option value="critical">Critical - App is unusable</option>
                    <option value="high">High - Major feature broken</option>
                    <option value="medium">Medium - Feature partially working</option>
                    <option value="low">Low - Minor issue or cosmetic</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Where did this happen? *
                </label>
                <select
                  value={bugForm.page}
                  onChange={(e) => setBugForm({ ...bugForm, page: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500"
                  required
                >
                  <option value="">Select page/feature...</option>
                  <option value="dashboard">Dashboard</option>
                  <option value="contacts">Contacts List</option>
                  <option value="contact-detail">Contact Detail</option>
                  <option value="tags">Tags Manager</option>
                  <option value="team-dashboard">Team Dashboard</option>
                  <option value="settings">Settings</option>
                  <option value="login">Login/Authentication</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Steps to Reproduce *
                </label>
                <textarea
                  value={bugForm.steps}
                  onChange={(e) => setBugForm({ ...bugForm, steps: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 resize-none"
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Expected Behavior
                  </label>
                  <textarea
                    value={bugForm.expected}
                    onChange={(e) => setBugForm({ ...bugForm, expected: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 resize-none"
                    placeholder="What should have happened?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Actual Behavior
                  </label>
                  <textarea
                    value={bugForm.actual}
                    onChange={(e) => setBugForm({ ...bugForm, actual: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 resize-none"
                    placeholder="What actually happened?"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Browser/Device Info
                </label>
                <input
                  type="text"
                  value={bugForm.browser}
                  onChange={(e) => setBugForm({ ...bugForm, browser: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500"
                  placeholder="Auto-detected"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Auto-filled from your browser. You can edit if needed.
                </p>
              </div>

              {submitError && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                  {submitError}
                </div>
              )}

              <div className="flex items-center gap-4 pt-2">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
                </Button>
                <span className="text-sm text-gray-500 dark:text-gray-400">* Required fields</span>
              </div>
            </form>
          )}
        </section>
      )}

      {/* Contact Support Section */}
      {activeTab === 'contact' && (
        <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">💬</span>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contact Support</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Can't find what you're looking for? Send us a message and we'll get back to you within 24 hours.
          </p>

          {submitted === 'contact' ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Message Sent!
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We've received your message and will respond within 24 hours.
              </p>
              <Button onClick={() => setSubmitted(null)}>Send Another Message</Button>
            </div>
          ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject
                </label>
                <select
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500"
                  required
                >
                  <option value="">Select a topic...</option>
                  <option value="feature">Feature Request</option>
                  <option value="account">Account Issue</option>
                  <option value="billing">Billing Question</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message
                </label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-ondeck-500 resize-none"
                  placeholder="Describe your issue or question..."
                  required
                />
              </div>
              {submitError && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm">
                  {submitError}
                </div>
              )}

              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          )}
        </section>
      )}

      {/* Additional Resources */}
      <section className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>
          Email us directly at{' '}
          <a href="mailto:support@ondeck.app" className="text-ondeck-600 dark:text-ondeck-400 hover:underline">
            support@ondeck.app
          </a>
        </p>
      </section>
    </div>
  );
}
